// @flow

import * as React from 'react';
import { FlatList, Pressable, View } from 'react-native';
import { pageStyles } from 'src/navigation/defaultPageStyles';
import Text from 'src/components/CustomText/CustomText';
import { FormattedMessage } from 'react-intl';
import styles from 'src/navigation/Profile/ProfileStyles';
import FilterChannelsInputsForm from 'src/navigation/Profile/FilterChannelsInputsForm';
import { useState } from 'react';
import RemainingChannelItem from 'src/navigation/Profile/RemainingChannelItem';

type Props = {
    remainingChannels: Array<Object>,
    addedChannels: Array<Object>,
    width: any,
    setAddedChannels: Function,
    setRemainingChannels: Function,
}

const RemainingChannelsTable = (props: Props) => {
    const {
        remainingChannels, width, setRemainingChannels, setAddedChannels, addedChannels,
    } = props;

    const [filterCategory, setFilterCategory] = useState('');
    const [filterResolution, setFilterResolution] = useState('');

    const getFilteredChannels = React.useCallback(() => {
        if (filterCategory && filterResolution) {
            return remainingChannels.filter((el) => el.category.code
                === filterCategory && el.resolution.code === filterResolution);
        }
        if (filterCategory) {
            return remainingChannels.filter((el) => el.category.code === filterCategory);
        }
        if (filterResolution) {
            return remainingChannels.filter((el) => el.resolution.code === filterResolution);
        }
        return null;
    }, [remainingChannels, filterCategory, filterResolution]);

    const addAllChannels = React.useCallback(() => {
        // if there are filtered channels add only them otherwise add all channels
        if (filterCategory || filterResolution) {
            // add the filtered channels to the state
            const filteredChannels = getFilteredChannels();
            setAddedChannels((prevAddedChannels) => [...prevAddedChannels, ...filteredChannels]);
            // get the filtered channels uuids
            const filteredChannelsUuid = filteredChannels.map((channel) => channel.uuid);
            // exclude the filtered channels from the remaining channels
            // and update the state
            const excludedChannels = remainingChannels.filter(
                (channel) => !filteredChannelsUuid.includes(channel.uuid),
            );
            setRemainingChannels(excludedChannels);
        } else {
            // if there are no filtered channels add all channels to the state
            setAddedChannels((prevAddedChannels) => [...prevAddedChannels, ...remainingChannels]);
            setRemainingChannels([]);
        }
    }, [
        filterCategory, filterResolution, getFilteredChannels, setAddedChannels, remainingChannels,
        setRemainingChannels,
    ]);

    const renderRemainingChannelItem = React.useCallback(({ item, index }) => (
        <RemainingChannelItem
            index={index}
            item={item}
            addedChannels={addedChannels}
            setAddedChannels={setAddedChannels}
            channels={remainingChannels}
            setRemainingChannels={setRemainingChannels}
        />
    ), [addedChannels, remainingChannels, setAddedChannels, setRemainingChannels]);

    return (
        <View style={width > 600 ? pageStyles.available : pageStyles.added}>
            <View style={width > 600 ? pageStyles.channels_label : pageStyles.channels_label_phone}>
                <Text style={width > 600 ? pageStyles.available_channels_label
                    : pageStyles.available_channels_label_phone}
                >
                    <FormattedMessage
                        id="available-channels-outside-of-profile"
                        defaultMessage="Available Channels Outside Of Profile"
                    />
                </Text>
                <Pressable
                    onPress={addAllChannels}
                    style={pageStyles.centered}
                >
                    <Text style={[width > 600 ? pageStyles.light_blue
                        : [pageStyles.light_blue, pageStyles.centered],
                    width < 600 && pageStyles.up_button]}
                    >
                        <FormattedMessage
                            id="add-all-channels"
                            defaultMessage="Add All Channels"
                        />
                    </Text>
                </Pressable>
            </View>
            <View style={width > 600 ? styles.filters_wrapper : styles.filters_wrapper_phone}>
                <Text style={styles.filter_text}>
                    <FormattedMessage
                        id="filter"
                        defaultMessage="Filter"
                    />
                </Text>
                <FilterChannelsInputsForm
                    filterCategory={filterCategory}
                    setFilterCategory={setFilterCategory}
                    filterResolution={filterResolution}
                    setFilterResolution={setFilterResolution}
                />
            </View>
            <View
                style={pageStyles.channel_label_container}
            >
                <View
                    style={width > 600
                        ? pageStyles.channel_label_first_column
                        : pageStyles.channel_label_first_column_phone}
                />
                <Text style={pageStyles.channel_label_second_column}>
                    <FormattedMessage
                        id="channel"
                        defaultMessage="Channel"
                    />
                </Text>
                {
                    width > 600 && (
                        <>
                            <Text style={width > 600 ? pageStyles.channel_label_third_column
                                : pageStyles.channel_label_container}
                            >
                                <FormattedMessage
                                    id="category"
                                    defaultMessage="Category"
                                />
                            </Text>
                            <Text style={width > 600 ? pageStyles.channel_label_fourth_column
                                : styles.added_channels_labels_phone}
                            >
                                <FormattedMessage
                                    id="resolution"
                                    defaultMessage="Resolution"
                                />
                            </Text>
                        </>
                    )
                }
                <View style={pageStyles.channel_label_fifth_column} />
            </View>
            <FlatList
                showsVerticalScrollIndicator={false}
                data={filterCategory || filterResolution ? getFilteredChannels() : remainingChannels}
                renderItem={renderRemainingChannelItem}
                keyExtractor={(item) => item.uuid}
                scrollEnabled={width < 960}
            />
        </View>
    );
};

export default RemainingChannelsTable;
